/**
 * CheckInMedicationsReview - Hosts MedicationsConversation within the SecureLink check-in flow.
 *
 * Loads the patient's medications (and optionally allergies, PMH, PSH, other)
 * from Firestore, provides CRUD callbacks, and renders the voice-led review.
 * When the review is complete (or the patient skips), calls onComplete().
 *
 * Props:
 *  - mode: 'medications' | 'full_cmr'
 *  - onComplete: () => void   — advance to the next check-in step
 *  - onSkip:     () => void   — skip the review
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import MedicationRounded from '@mui/icons-material/MedicationRounded';
import SkipNextRounded from '@mui/icons-material/SkipNextRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { useAuth } from '@healthdesk/shared-hooks';
import { db } from '@healthdesk/shared-services';
import { fetchPatientCMR } from '../../features/chartmind/components/chartmind/services/cmrReadService';
import { medicationService } from 'services/medicationService';
import MedicationsConversation from '../../features/patient/reviewHistory/MedicationsConversation';

export default function CheckInMedicationsReview({ mode = 'medications', onComplete, onSkip }) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [pmh, setPmh] = useState([]);
  const [psh, setPsh] = useState([]);
  const [other, setOther] = useState({
    socialHistory: '',
    smokingStatus: '',
    alcoholUse: '',
    drugUse: '',
    occupation: '',
    familyHistory: '',
    additionalNotes: '',
  });
  const [currentMedIndex, setCurrentMedIndex] = useState(0);
  const [reviewDone, setReviewDone] = useState(false);

  const onEditDoneRef = useRef(null);

  const medicationsOnly = mode === 'medications';

  // Load patient data
  const loadData = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const [cmr, meds] = await Promise.all([
        fetchPatientCMR(user.uid),
        medicationService.getMedications(user.uid),
      ]);
      setMedications(meds || []);
      const hr = cmr || {};
      setAllergies(Array.isArray(hr.allergies) ? hr.allergies : []);
      setPmh(Array.isArray(hr.pmh) ? hr.pmh : []);
      setPsh(Array.isArray(hr.psh) ? hr.psh : []);
      setOther(
        hr.other && typeof hr.other === 'object'
          ? {
              socialHistory: hr.other.socialHistory || '',
              smokingStatus: hr.other.smokingStatus || '',
              alcoholUse: hr.other.alcoholUse || '',
              drugUse: hr.other.drugUse || '',
              occupation: hr.other.occupation || '',
              familyHistory: hr.other.familyHistory || '',
              additionalNotes: hr.other.additionalNotes || '',
            }
          : {
              socialHistory: '',
              smokingStatus: '',
              alcoholUse: '',
              drugUse: '',
              occupation: '',
              familyHistory: '',
              additionalNotes: '',
            },
      );
    } catch (err) {
      console.error('[CheckInMedicationsReview] Load error:', err);
      setError(err.message || 'Failed to load your records.');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (medications.length > 0 && currentMedIndex >= medications.length) {
      setCurrentMedIndex(Math.max(0, medications.length - 1));
    }
  }, [medications.length, currentMedIndex]);

  // CRUD callbacks — persist changes to patient records in Firestore
  const handleMedicationDeleted = useCallback(async (medicationId) => {
    if (!user?.uid) return;
    try {
      await medicationService.deleteMedication(user.uid, medicationId);
      setMedications((prev) => prev.filter((m) => m.id !== medicationId));
      setCurrentMedIndex((i) => Math.max(0, Math.min(i, medications.length - 2)));
    } catch (err) {
      console.error('[CheckInMedicationsReview] Delete error:', err);
    }
  }, [user?.uid, medications.length]);

  const handleMedicationUpdated = useCallback(async (medicationId, data) => {
    if (!user?.uid) return;
    try {
      await medicationService.updateMedication(user.uid, medicationId, data);
      setMedications((prev) => prev.map((m) => (m.id === medicationId ? { ...m, ...data } : m)));
    } catch (err) {
      console.error('[CheckInMedicationsReview] Update error:', err);
    }
  }, [user?.uid]);

  const handleMedicationAdded = useCallback(async (data) => {
    if (!user?.uid) return;
    try {
      const created = await medicationService.createMedication(user.uid, data);
      setMedications((prev) => [...prev, { ...data, id: created.id }]);
    } catch (err) {
      console.error('[CheckInMedicationsReview] Add error:', err);
    }
  }, [user?.uid]);

  const handleAddFromVoice = useCallback((draft) => {
    if (!draft || !user?.uid) return;
    handleMedicationAdded(draft);
  }, [user?.uid, handleMedicationAdded]);

  // Health records persistence for full CMR mode
  const saveHealthRecordsSection = useCallback(async (key, value) => {
    if (!user?.uid) return;
    try {
      const userDoc = await db.collection('users').doc(user.uid).get();
      const existingHealthRecords = userDoc.exists ? userDoc.data()?.healthRecords || {} : {};
      await db.collection('users').doc(user.uid).set(
        { healthRecords: { ...existingHealthRecords, [key]: value, updatedAt: new Date().toISOString() } },
        { merge: true },
      );
    } catch (err) {
      console.error('[CheckInMedicationsReview] Save healthRecords error:', err);
    }
  }, [user?.uid]);

  const handleAllergiesUpdate = useCallback(async (v) => { setAllergies(v); await saveHealthRecordsSection('allergies', v); }, [saveHealthRecordsSection]);
  const handlePmhUpdate = useCallback(async (v) => { setPmh(v); await saveHealthRecordsSection('pmh', v); }, [saveHealthRecordsSection]);
  const handlePshUpdate = useCallback(async (v) => { setPsh(v); await saveHealthRecordsSection('psh', v); }, [saveHealthRecordsSection]);
  const handleOtherUpdate = useCallback(async (v) => { setOther(v); await saveHealthRecordsSection('other', v); }, [saveHealthRecordsSection]);

  // Called by MedicationsConversation when it reaches the 'done' state
  const handleConversationDone = useCallback(() => {
    setReviewDone(true);
  }, []);

  // No-op callbacks for props MedicationsConversation expects but we don't need
  const noop = useCallback(() => {}, []);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading your medical records...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={onSkip}>Skip and Continue</Button>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <MedicationRounded color="primary" />
        <Typography variant="h6">
          {medicationsOnly ? 'Review Your Medications' : 'Review Your Medical Records'}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {medicationsOnly
          ? 'Please review your current medications to make sure they are up to date before your visit.'
          : 'Please review your medications, allergies, and medical history to make sure everything is up to date.'}
      </Typography>

      <MedicationsConversation
        medications={medications}
        currentMedIndex={currentMedIndex}
        onCurrentIndexChange={setCurrentMedIndex}
        onDelete={handleMedicationDeleted}
        onUpdate={handleMedicationUpdated}
        onAdd={handleMedicationAdded}
        onRefreshMedications={loadData}
        onRequestAddOpen={noop}
        onRequestScanOpen={noop}
        onMedicationsPhaseChange={noop}
        onSectionChange={noop}
        onRequestEditOpen={noop}
        onEditDoneFromVoice={noop}
        onAddDoneFromVoice={noop}
        onEditingDraftChange={noop}
        onAddDraftChange={noop}
        onAddFromVoice={handleAddFromVoice}
        onHighlightAddField={noop}
        onHighlightEditField={noop}
        allergies={allergies}
        pmh={pmh}
        psh={psh}
        other={other}
        onAllergiesUpdate={handleAllergiesUpdate}
        onPmhUpdate={handlePmhUpdate}
        onPshUpdate={handlePshUpdate}
        onOtherUpdate={handleOtherUpdate}
        medicationsOnly={medicationsOnly}
        onDone={handleConversationDone}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} pt={2} sx={{ borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="text"
          color="inherit"
          startIcon={<SkipNextRounded />}
          onClick={onSkip}
        >
          Skip Review
        </Button>
        {reviewDone && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleRounded />}
            onClick={onComplete}
          >
            Continue to Check-in
          </Button>
        )}
      </Box>
    </Paper>
  );
}
